import { Component, OnInit, OnDestroy, HostListener, ElementRef } from '@angular/core';
import { MlTranslationService } from 'src/app/services/ml-translation.service';
import { Router, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { LangService } from 'src/app/services/lang.service';
import { AuthService } from 'src/app/services/auth.service';
import { UsersService } from 'src/app/services/users.service';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { InitService } from 'src/app/services/init.service';

interface Language {
  code: string;
  name: string;
}

@Component({
  selector: 'app-ml-translation-widget',
  templateUrl: './ml-translation-widget.component.html',
  styleUrls: ['./ml-translation-widget.component.scss']
})
export class MlTranslationWidgetComponent implements OnInit, OnDestroy {
  public enabled = false;
  public selectedLanguage = 'en';
  public isDropdownOpen = false;
  public shouldShowWidget = true;
  public isMinimized = false;

  public languages: Language[] = [
    { code: 'nl', name: 'Dutch' },
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'Française' },
    { code: 'es', name: 'Español' },
    { code: 'it', name: 'Italiano' },
    { code: 'de', name: 'Deutsche' },
    { code: 'pt', name: 'Portugues' }
  ];

  private destroy$ = new Subject<void>();

  private user: any = null;

  constructor(
    private mlTranslationService: MlTranslationService,
    private elementRef: ElementRef,
    private router: Router,
    private langService: LangService,
    private authService: AuthService,
    private usersService: UsersService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private initService: InitService
  ) {
    this.authService.user.pipe(takeUntil(this.destroy$)).subscribe((userData) => {
      this.user = userData;
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside && this.isDropdownOpen) {
      this.isDropdownOpen = false;
    }
  }

  ngOnInit(): void {
    this.mlTranslationService.getState()
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.enabled = state.enabled;
        this.selectedLanguage = state.selectedLanguage;
      });

    // Sync with the main language service (to match dropdownUserLanguage)
    this.langService.language
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => {
        if (lang) {
          // Update the selected language to match the main language
          const matchingLang = this.languages.find(l => l.code.toLowerCase() === lang.toLowerCase());
          if (matchingLang && this.selectedLanguage !== matchingLang.code) {
            this.selectedLanguage = matchingLang.code;
            this.mlTranslationService.setLanguage(matchingLang.code);
          }
        }
      });

    // Check initial route
    this.checkRoute(this.router.url);

    // Listen to route changes
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: any) => {
        this.checkRoute(event.urlAfterRedirects || event.url);
      });
  }

  private checkRoute(url: string): void {
    // Hide widget on login, signup, reset password, and auth/surf pages
    const hiddenRoutes = ['/login', '/signup', '/reset_password', '/auth/surf'];
    this.shouldShowWidget = !hiddenRoutes.some(route => url.includes(route));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public toggleEnabled(): void {
    this.enabled = !this.enabled;
    this.mlTranslationService.setEnabled(this.enabled);
    if (!this.enabled) {
      this.isDropdownOpen = false;
      // Reset to platform's default language
      const platformLang = this.initService.getCurrentOrg()?.language || 'nl';
      this.langService.setLanguage(platformLang);
      this.mlTranslationService.setLanguage(platformLang);
      window.location.reload();
    }
  }

  public selectLanguage(language: Language): void {
    if (this.selectedLanguage === language.code) {
      this.isDropdownOpen = false;
      return;
    }
    this.selectedLanguage = language.code;
    this.mlTranslationService.setLanguage(language.code);

    // Also update the main language service (like dropdownUserLanguage does)
    this.langService.setLanguage(language.code);

    this.isDropdownOpen = false;

    // Save to user profile if authenticated, then reload
    if (this.authService.isAuthenticated() && this.user) {
      this.usersService
        .patchInfoAccount(this.user.username, {
          country_code: null,
          language: language.code.toLowerCase(),
        })
        .then((res: any) => {
          this.toastService.showSuccess(
            this.translateService.instant('profile:change:language:success')
          );
          window.location.reload();
        })
        .catch((err) => {
          this.toastService.showError(err);
          window.location.reload();
        });
    } else {
      window.location.reload();
    }
  }

  public toggleDropdown(event: MouseEvent): void {
    event.stopPropagation();
    if (this.enabled) {
      this.isDropdownOpen = !this.isDropdownOpen;
    }
  }

  public getSelectedLanguageName(): string {
    const language = this.languages.find(lang => lang.code === this.selectedLanguage);
    return language ? language.name : 'English';
  }

  public toggleMinimized(): void {
    this.isMinimized = !this.isMinimized;
    if (this.isMinimized) {
      this.isDropdownOpen = false;
    }
  }
}
